import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TenantDto } from '../types/tenant';
import type { ProjectDto } from '../types/project';
import { tenantService } from '../services/tenantService';
import { projectService } from '../services/projectService';
import { getModules } from '../services/appConfigService';
import { useAuthStore } from './authStore';
import { hasGlobalAccess } from '../utils/globalAccess';

/** Enabled feature modules (default all true until first fetch). */
export interface EnabledModules {
  automation: boolean;
  governance: boolean;
  integrations: boolean;
}

interface ContextState {
  currentTenant: TenantDto | null;
  currentProject: ProjectDto | null;
  availableTenants: TenantDto[];
  availableProjects: ProjectDto[];
  enabledModules: EnabledModules;
  globalAccessRoles: string[];
  isGlobalAccess: boolean;
  isLoading: boolean;
  error: string | null;
  setTenant: (tenant: TenantDto | null) => void;
  setProject: (project: ProjectDto | null) => void;
  loadTenants: () => Promise<void>;
  loadProjects: (tenantId: string) => Promise<void>;
  loadModules: () => Promise<void>;
  evaluateGlobalAccess: (token?: string | null) => void;
  clearContext: () => void;
}

/**
 * Context store for managing tenant and project state.
 */
export const useContextStore = create<ContextState>()(
  persist(
    (set, get) => ({
      currentTenant: null,
      currentProject: null,
      availableTenants: [],
      availableProjects: [],
      enabledModules: { automation: true, governance: true, integrations: true },
      globalAccessRoles: [],
      isGlobalAccess: false,
      isLoading: false,
      error: null,

      setTenant: (tenant: TenantDto | null) => {
        set({ currentTenant: tenant, currentProject: null });
        // Clear projects when tenant changes
        set({ availableProjects: [] });
        // Load projects for new tenant if tenant is set
        if (tenant) {
          get().loadProjects(tenant.id);
        }
      },

      setProject: (project: ProjectDto | null) => {
        set({ currentProject: project });
      },

      loadTenants: async () => {
        const user = useAuthStore.getState().user;
        if (!user) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const tenants = await tenantService.getUserTenants();
          set({ availableTenants: tenants, isLoading: false });
          // Default to first tenant when none is selected
          if (!get().currentTenant && tenants.length > 0) {
            get().setTenant(tenants[0]);
          }
        } catch (error: any) {
          // Silently handle errors when backend is unavailable (500 errors)
          // Only set error for other types of errors
          const isBackendUnavailable = error.response?.status === 500;
          set({ 
            error: isBackendUnavailable ? null : (error.response?.data?.message || error.message || 'Failed to load tenants'),
            isLoading: false,
            availableTenants: isBackendUnavailable ? [] : get().availableTenants,
          });
        }
      },

      loadProjects: async (tenantId: string) => {
        const user = useAuthStore.getState().user;
        if (!user) {
          set({ error: 'User not authenticated' });
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const projects = await projectService.getUserProjects(tenantId);
          set({ availableProjects: projects, isLoading: false });
        } catch (error: any) {
          // Silently handle errors when backend is unavailable (500 errors)
          // Only set error for other types of errors
          const isBackendUnavailable = error.response?.status === 500;
          set({ 
            error: isBackendUnavailable ? null : (error.response?.data?.message || error.message || 'Failed to load projects'),
            isLoading: false,
            availableProjects: isBackendUnavailable ? [] : get().availableProjects,
          });
        }
      },

      loadModules: async () => {
        try {
          const modules = await getModules();
          set({
            enabledModules: {
              automation: modules.automation,
              governance: modules.governance,
              integrations: modules.integrations,
            },
            globalAccessRoles: modules.globalAccessRoles,
          });
          get().evaluateGlobalAccess(useAuthStore.getState().token);
          if (import.meta.env.DEV) {
            console.log('[contextStore] enabledModules loaded from API', modules);
          }
        } catch (err) {
          // Keep default (all true) when backend is unavailable
          if (import.meta.env.DEV) {
            console.warn('[contextStore] loadModules failed (menu will show all modules)', err);
          }
        }
      },

      evaluateGlobalAccess: (token?: string | null) => {
        const authToken = token ?? useAuthStore.getState().token;
        const allowedRoles = get().globalAccessRoles;
        set({ isGlobalAccess: hasGlobalAccess(authToken, allowedRoles) });
      },

      clearContext: () => {
        set({
          currentTenant: null,
          currentProject: null,
          availableTenants: [],
          availableProjects: [],
          error: null,
          isGlobalAccess: false,
          // Do not reset enabledModules; they are loaded from server on app init
        });
      },
    }),
    {
      name: 'agentops-context-storage',
      partialize: (state) => ({
        currentTenant: state.currentTenant,
        currentProject: state.currentProject,
      }),
    }
  )
);
